import { z } from 'zod';
import type { StringKeyOf } from 'type-fest';
import type { Database } from '~/models/db';
import { IsoDateStringSchema } from '~/util/zod';

export type OrganizationRowDb =
  Database['public']['Tables']['organization']['Row'];

export const OrganizationRowSchema = z.object({
  organization_id: z.string().uuid(),
  created_at: IsoDateStringSchema,
  name: z.string().min(1),
  deleted_at: IsoDateStringSchema.nullable(),
  invitation_code: z.string().min(1).nullable(),
}) satisfies z.ZodType<OrganizationRowDb>;

export type OrganizationRow = z.infer<typeof OrganizationRowSchema>;

export const OrganizationIdKey: StringKeyOf<OrganizationRow> &
  'organization_id' = 'organization_id';
export type OrganizationRowId = OrganizationRow['organization_id'];

/**
 * Indicates if the given item contains a valid id, likely meaning it exists in the database.
 */
export function isOrganizationWithId(itemData: Partial<OrganizationRow>) {
  return (
    Object.keys(itemData).includes(OrganizationIdKey) &&
    OrganizationRowSchema.shape[OrganizationIdKey].safeParse(
      itemData[OrganizationIdKey],
    ).success
  );
}

/**
 * Gets a single organization record.
 */
export default async function useOrganization(
  id: OrganizationRowId,
): Promise<OrganizationRow | undefined> {
  let item: OrganizationRow | undefined;

  if (id) {
    const sb = useSupabaseClient<Database>();
    const result = await sb
      .from('organization')
      .select('*')
      .eq('organization_id', `${id}`)
      .returns<OrganizationRow[]>()
      .limit(1)
      .single();

    if (result.error) {
      throw result.error;
    }

    item = OrganizationRowSchema.parse(result.data);
  }

  return item;
}
